import { Good } from "@/entities/good";
import { GoodVersion } from "@/entities/goodVersion";
import { getGoodCharacteristics, WbCharacteristic } from "./getGoodCharacteristics";
import { getWbColorsMap, sanitizeColorAgainstWbDictionary } from "./getWbColors";
import { wbFetch } from "./wbFetch";
import { setWbGoodId } from "../setWbGoodId";
import { wbUploadMedia } from "./wbUploadMedia";
import { getWbPublishedGoods } from "./getWbPublishedGoods";
import { getWbErroredGoods } from "./getWbErroredGoods";
import { resolveWbCategory } from "./resolveWbCategory";
import { geniFetch } from "../geniBackend";
import { orchestrateWbContentApiBodyFix } from "../geniAiOrchestration";

const generateSKU = () => {
  return `wb${Math.random().toString(36).slice(2)}`;
};

const WB_CARDS_MUTATION_MAX_ATTEMPTS = 3;

function isWbContentCardsMutationError(res: unknown): boolean {
  return typeof res === "object" && res !== null && (res as { error?: boolean }).error === true;
}

function wbContentCardsErrorMessage(res: unknown): string {
  if (typeof res === "object" && res !== null && "errorText" in res) {
    const t = (res as { errorText: unknown }).errorText;
    if (typeof t === "string" && t.trim()) {
      return t.trim();
    }
  }
  try {
    return JSON.stringify(res);
  } catch {
    return "Неизвестная ошибка API Wildberries";
  }
}

function readWbAdditionalErrors(res: unknown): Record<string, unknown> | undefined {
  if (typeof res !== "object" || res === null) {
    return undefined;
  }
  const ae = (res as { additionalErrors?: unknown }).additionalErrors;
  if (typeof ae === "object" && ae !== null && !Array.isArray(ae)) {
    return ae as Record<string, unknown>;
  }
  return undefined;
}

function assertWbCardsPostBody(body: unknown): any[] {
  if (!Array.isArray(body)) {
    throw new Error("ИИ вернул неверное тело: ожидался массив карточек для Content API Wildberries.");
  }
  return body;
}

/**
 * POST к /content/v2/cards/upload или /cards/update: при error:true до 3 попыток с правкой тела через geni-backend ИИ.
 */
async function postWbContentCardsMutationWithAiRecovery(url: string, initialBody: any[]): Promise<void> {
  let body: any[] = initialBody;
  for (let attempt = 1; attempt <= WB_CARDS_MUTATION_MAX_ATTEMPTS; attempt++) {
    const response = await wbFetch(url, "POST", JSON.stringify(body));
    if (!isWbContentCardsMutationError(response)) {
      return;
    }
    const errorText = wbContentCardsErrorMessage(response);
    if (attempt >= WB_CARDS_MUTATION_MAX_ATTEMPTS) {
      throw new Error(`Публикация на Wildberries не удалась: ${errorText}`);
    }
    const additionalErrors = readWbAdditionalErrors(response);
    const { requestBody } = await orchestrateWbContentApiBodyFix({
      requestBody: body,
      errorText,
      ...(additionalErrors && Object.keys(additionalErrors).length > 0 ? { additionalErrors } : {}),
    });
    body = assertWbCardsPostBody(requestBody);
  }
}

export const publicateGoodVersion = async (good: Good, version: GoodVersion) => {
  const subjectID = await resolveSubjectId(good, version);
  const characteristicsSchema = await getGoodCharacteristics(subjectID);

  const characteristics = await getFormattedCharacteristics([...good.characteristics], characteristicsSchema);

  if (!good.publicationData?.wb?.nmId) {
    const sku = good.publicationData?.wb?.id || generateSKU();

    await createGoodCard(good, version, characteristics, sku);

    good.publicationData.wb = { id: sku };

    await setWbGoodId(good, sku);

    // Пока что ретерним, потом надо как-то сразу отслеживать ошибки, чтобы сразу загрузить фотки

    const attempts = 5;

    for (let attempt = 0; attempt < attempts; attempt++) {
      await new Promise((resolve) => setTimeout(resolve, 5000));

      const publishedGoods = await getWbPublishedGoods();

      const publishedGood = publishedGoods.find((g) => g.vendorCode === sku);

      if (publishedGood) {
        good.publicationData.wb.nmId = publishedGood.nmID;
        await setWbGoodId(good, sku, publishedGood.nmID);

        break;
      }

      const erroredGoods = await getWbErroredGoods();

      const erroredGood = erroredGoods.find((g) => g.vendorCodes.includes(sku));

      if (erroredGood) {
        // console.log(erroredGood);
        // console.log("ERROR");
        return;
      }
    }

    await new Promise((resolve) => setTimeout(resolve, 5000));
  } else {
    await updateGoodCard(good, version, characteristics);
  }

  await wbUploadMedia(good, version);
};

const normalizeCategoryForWb = (category: Good["category"] | string | null | undefined): NonNullable<Good["category"]> => {
  if (category && typeof category === "object" && !Array.isArray(category)) {
    return category as NonNullable<Good["category"]>;
  }

  if (typeof category === "string" && category.trim()) {
    return {
      wb: {
        name: category.trim(),
        id: 0,
      },
    };
  }

  return {};
};

const resolveSubjectId = async (good: Good, version: GoodVersion): Promise<number> => {
  const existingSubjectId = good.category?.wb?.id || good.publicationData?.wb?.subjectId;
  if (existingSubjectId) {
    good.publicationData.wb = good.publicationData.wb || {};
    good.publicationData.wb.subjectId = existingSubjectId;
    return existingSubjectId;
  }

  const title = version.title?.trim();
  if (!title) {
    throw new Error("Невозможно определить категорию WB: отсутствует название товара.");
  }

  // GC-23: передаём description вместе с title. С коротким (2-6 слов) title компьютер
  // часто путает БАД «NOW Selenium 100 мкг, 100 таблеток» с «Салфетки медицинские».
  // Описание из карточки даёт нужный контекст.
  const description = version.description?.trim() || undefined;
  const resolvedCategory = await resolveWbCategory(title, description);
  const resolvedSubjectId = resolvedCategory.best?.id;
  if (!resolvedSubjectId) {
    throw new Error("Не удалось определить категорию WB перед публикацией.");
  }

  good.category = normalizeCategoryForWb(good.category);
  good.category.wb = {
    ...(good.category.wb || { name: resolvedCategory.best?.name || "" }),
    id: resolvedSubjectId,
  };
  good.publicationData.wb = good.publicationData.wb || {};
  good.publicationData.wb.subjectId = resolvedSubjectId;

  await geniFetch(`/goods/${good.id}`, {
    method: "PATCH",
    body: JSON.stringify({
      metadata: {
        wb: {
          ...(good.publicationData.wb || {}),
          subjectId: resolvedSubjectId,
        },
      },
    }),
  });

  return resolvedSubjectId;
};

const updateGoodCard = async (good: Good, version: GoodVersion, characteristics: { id: number; value: any }[]) => {
  const body: any = [
    {
      nmID: good.publicationData.wb.nmId,
      vendorCode: good.publicationData.wb.id,
      brand: good.brand,
      title: version.title,
      description: version.description,
      characteristics: characteristics,
      sizes: good.publicationData.wb.sizes,
    },
  ];

  const url = "https://content-api.wildberries.ru/content/v2/cards/update";

  await postWbContentCardsMutationWithAiRecovery(url, body);
};

const createGoodCard = async (good: Good, version: GoodVersion, characteristics: { id: number; value: any }[], sku: string = "") => {
  const subjectID = good.category?.wb?.id || good.publicationData?.wb?.subjectId;
  if (!subjectID) {
    throw new Error("Невозможно создать карточку WB без subjectID.");
  }

  if (!sku) {
    sku = generateSKU();
  }

  const body: any = [
    {
      subjectID,
      variants: [
        {
          brand: good.brand,
          title: version.title,
          description: version.description,
          vendorCode: sku,
          characteristics: characteristics,
        },
      ],
    },
  ];

  const url = "https://content-api.wildberries.ru/content/v2/cards/upload";

  await postWbContentCardsMutationWithAiRecovery(url, body);
};

/** Не отправляем в WB Content API: вес переносится в dimensions.weightBrutto и дубль ломает валидацию. */
const WB_PUBLISH_EXCLUDED_CHARACTERISTIC_NAMES = new Set<string>(["Вес товара с упаковкой (г)"]);

const WB_COLOR_CHARACTERISTIC_NAMES = new Set<string>(["Цвет", "Цвета"]);

const isColorCharacteristic = (name: string): boolean =>
  WB_COLOR_CHARACTERISTIC_NAMES.has(name);

const loadWbColorsSafely = async (): Promise<Map<string, string> | null> => {
  try {
    return await getWbColorsMap();
  } catch (error) {
    console.warn("[publicateGoodVersion] failed to load WB colors directory; sending raw color value", error);
    return null;
  }
};

const getFormattedCharacteristics = async (
  savedCharacteristics: { name: string; value: string }[],
  characteristicsSchema: WbCharacteristic[]
): Promise<{ id: number; value: any }[]> => {
  const needsColorDictionary = characteristicsSchema.some(
    (c) => isColorCharacteristic(c.name) && c.charcType === 1
  );
  const colorDictionary = needsColorDictionary ? await loadWbColorsSafely() : null;

  return characteristicsSchema
    .filter((c) => !WB_PUBLISH_EXCLUDED_CHARACTERISTIC_NAMES.has(c.name))
    .map((characteristic) => {
      const savedCharacteristic = savedCharacteristics.find((item) => item.name === characteristic.name);

      if (!savedCharacteristic?.value) {
        return {
          id: characteristic.charcID,
          value: "",
        };
      }

      let formattedCharacteristicValue: number | string[] | null = null;

      if (characteristic.charcType === 1) {
        if (typeof savedCharacteristic.value === "string") {
          if (isColorCharacteristic(characteristic.name) && colorDictionary) {
            const validated = sanitizeColorAgainstWbDictionary(savedCharacteristic.value, colorDictionary);
            formattedCharacteristicValue = validated.length > 0 ? validated : null;
          } else {
            formattedCharacteristicValue = savedCharacteristic.value.split(";").map((item) => item.trim());
          }
        } else {
          formattedCharacteristicValue = savedCharacteristic.value;
        }
      } else if (characteristic.charcType === 4) {
        formattedCharacteristicValue = parseInt(savedCharacteristic.value) | 0;
      }

      return {
        id: characteristic.charcID,
        value: formattedCharacteristicValue,
      };
    })
    .filter((item) => item.value);
};
